import { AppLayout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { useEffect, useState, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { ArrowLeft, Video, Loader2, AlertTriangle } from "lucide-react";

interface VideoInfo {
  roomUrl: string;
  token: string | null;
  roomName: string;
  isHost: boolean;
}

export default function VideoSession() {
  const [, params] = useRoute("/video/:sessionId");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const sessionId = params?.sessionId;
  const backPath = user?.role === "TRAINER" ? "/trainer/sessions" : "/sessions";
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    apiFetch(`/sessions/${sessionId}/video`)
      .then((data: unknown) => {
        setVideoInfo(data as VideoInfo);
        setError(null);
      })
      .catch((err) => {
        setError(err.message || "Failed to load video session");
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  const dailyUrl = videoInfo
    ? videoInfo.token
      ? `${videoInfo.roomUrl}?t=${videoInfo.token}`
      : videoInfo.roomUrl
    : null;

  return (
    <AppLayout>
      <div className="flex flex-col h-[calc(100vh-80px)]">
        <div className="flex items-center justify-between py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            className="text-muted-foreground hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sessions
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Video className="w-4 h-4" />
            <span>{videoInfo?.isHost ? "Host" : "Participant"}</span>
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading video session...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Unable to Join</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button onClick={() => navigate(backPath)} className="bg-primary text-primary-foreground">
                Back to Sessions
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && dailyUrl && (
          <iframe
            ref={iframeRef}
            src={dailyUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="flex-1 w-full rounded-xl border border-white/10 bg-black"
            style={{ minHeight: "500px" }}
          />
        )}
      </div>
    </AppLayout>
  );
}
